import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const isAr = i18n.language === 'ar';
  const token = params.get('token') || '';
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ password: '', full_name: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError(isAr ? 'رابط الدعوة غير صالح' : 'Invalid invite link'); setLoading(false); return; }
    axios.get(`${API}/auth/invite-info/${token}`)
      .then(res => { setInvite(res.data); setForm(f => ({ ...f, full_name: res.data.full_name || '' })); })
      .catch(err => setError(err?.response?.data?.detail || (isAr ? 'الدعوة منتهية أو غير موجودة' : 'Invitation expired or not found')))
      .finally(() => setLoading(false));
  }, [token, isAr]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.password || form.password.length < 8) {
      setError(isAr ? 'كلمة المرور 8 أحرف على الأقل' : 'Password must be 8+ characters');
      return;
    }
    setSubmitting(true);
    try {
      if (invite.flow === 'first_login') {
        // Platform-issued invite — user already exists, just set the password.
        const r = await axios.post(`${API}/auth/accept-invite/first-login`, {
          token, new_password: form.password,
        });
        localStorage.setItem('token', r.data.access_token);
        // Use role for landing
        const role = r.data.user?.custom_role || r.data.user?.role;
        const landing = role === 'tenant' ? '/tenant' : role === 'vendor' ? '/vendor' : '/dashboard';
        navigate(landing, { replace: true });
        // Reload so AuthContext picks up the token
        window.location.reload();
      } else {
        // Legacy invitation — creates user.
        await axios.post(`${API}/auth/accept-invite`, { token, ...form });
        const r = await login(invite.email, form.password);
        if (r.success) {
          const role = r.permissions?.role;
          navigate(role === 'tenant' ? '/tenant' : '/dashboard');
        } else navigate('/auth');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || (isAr ? 'فشل قبول الدعوة' : 'Failed to accept invitation'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8] p-6" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-primary">
          <div className="w-9 h-9 gradient-primary rounded-xl flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-xl font-bold">REVA</span>
        </button>
        <Card className="shadow-glass border-gray-100">
          <CardContent className="pt-8 pb-8 px-8">
            {loading ? (
              <div className="text-center py-10"><Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" /></div>
            ) : error && !invite ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="h-7 w-7 text-red-500" />
                </div>
                <h2 className="font-heading text-lg font-bold text-gray-900 mb-1">{isAr ? 'دعوة غير صالحة' : 'Invitation invalid'}</h2>
                <p className="text-sm text-gray-500 mb-4">{error}</p>
                <Button onClick={() => navigate('/auth')} className="rounded-xl gradient-primary text-white">{isAr ? 'الذهاب لتسجيل الدخول' : 'Go to login'}</Button>
              </div>
            ) : invite && (
              <>
                <div className="mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-3">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <h1 className="font-heading text-xl font-bold text-gray-900">{isAr ? 'مرحباً بك في' : 'Welcome to'} {invite.organization_name || 'REVA'}</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {invite.flow === 'first_login'
                      ? (isAr ? 'حسابك جاهز — ضع كلمة مرور لتفعيله. الدور: ' : 'Your account is ready — set a password to activate it. Role: ')
                      : (`${invite.invited_by_name || ''} ${isAr ? 'دعاك للانضمام كـ' : 'invited you as'} `)}
                    <b className="capitalize">{(invite.role || '').replace(/_/g, ' ')}</b>.
                  </p>
                </div>
                {invite.message && <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-900 mb-5">{invite.message}</div>}
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">{isAr ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <Input value={invite.email} disabled className="mt-1 rounded-xl bg-gray-50" data-testid="accept-email" />
                  </div>
                  {invite.flow !== 'first_login' && (
                    <div>
                      <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الاسم الكامل' : 'Full name'}*</Label>
                      <Input required value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="mt-1 rounded-xl" data-testid="accept-name" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">{isAr ? 'كلمة مرور (8+ أحرف)' : 'Password (8+ chars)'}*</Label>
                    <Input type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" data-testid="accept-password" minLength={8} />
                  </div>
                  {invite.flow !== 'first_login' && (
                    <div>
                      <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الجوال' : 'Phone'}</Label>
                      <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" placeholder="+966" />
                    </div>
                  )}
                  {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="accept-error">{error}</div>}
                  <Button type="submit" disabled={submitting} className="w-full gradient-primary text-white rounded-xl h-11" data-testid="accept-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{invite.flow === 'first_login' ? (isAr ? 'تفعيل الحساب' : 'Activate account') : (isAr ? 'قبول وإنشاء الحساب' : 'Accept & create account')} <ArrowRight className="h-4 w-4 ms-1" /></>}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
